import { useEffect, useMemo, useState } from "react";

type Scenario = {
  id: "website" | "instagram" | "messenger";
  label: string;
  customer: string;
  answer: string;
  action: string;
  context: string;
};

const scenarios: Scenario[] = [
  {
    id: "website",
    label: "Website",
    customer: "Do you have the olive linen shirt in medium?",
    answer: "Medium is available now. I can share the product or help you choose the right size.",
    action: "View product",
    context: "Product interest saved with the contact",
  },
  {
    id: "instagram",
    label: "Instagram",
    customer: "Can I pick this up tomorrow?",
    answer: "Tomorrow pickup is available after 11am. I can collect your details so the team can confirm it.",
    action: "Share details",
    context: "Pickup intent is ready for follow-up",
  },
  {
    id: "messenger",
    label: "Messenger",
    customer: "I need to change the delivery address on my order.",
    answer: "I can ask the team to check that before dispatch. What order email should they look for?",
    action: "Notify team",
    context: "Human handoff prepared with the history attached",
  },
];

export function ProductDemo({ compact = false }: { compact?: boolean }) {
  const [selectedId, setSelectedId] = useState<Scenario["id"]>("website");
  const [thinking, setThinking] = useState(false);
  const scenario = useMemo(() => scenarios.find((item) => item.id === selectedId) ?? scenarios[0], [selectedId]);

  useEffect(() => {
    if (!thinking) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setThinking(false);
      return;
    }
    const timer = window.setTimeout(() => setThinking(false), 720);
    return () => window.clearTimeout(timer);
  }, [thinking, selectedId]);

  function selectScenario(id: Scenario["id"]) {
    setSelectedId(id);
    setThinking(true);
  }

  return (
    <div className={`product-demo ${compact ? "product-demo--compact" : ""}`} aria-label="Interactive Hello Marky Chat product demonstration">
      <div className="demo-topbar">
        <div className="demo-brand"><img src="/brand/marky-solid.svg" alt="" aria-hidden="true" /><span><strong>Hello Marky Chat</strong><small>Sample product experience</small></span></div>
        <button type="button" onClick={() => setThinking(true)}>Replay</button>
      </div>
      <div className="demo-tabs" role="tablist" aria-label="Conversation channel">
        {scenarios.map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={item.id === selectedId}
            key={item.id}
            className={item.id === selectedId ? "is-active" : ""}
            onClick={() => selectScenario(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="demo-workspace">
        <div className="demo-conversation" role="tabpanel">
          <div className="demo-customer"><span>Customer</span><p>{scenario.customer}</p></div>
          <div className="demo-agent-row" aria-live="polite">
            <img src="/brand/marky-solid.svg" alt="" aria-hidden="true" />
            {thinking ? (
              <div className="demo-typing" aria-label="Marky is preparing a reply"><i /><i /><i /></div>
            ) : (
              <div className="demo-agent-message">
                <span>Marky</span>
                <p>{scenario.answer}</p>
                <button type="button">{scenario.action} <i aria-hidden="true">↗</i></button>
              </div>
            )}
          </div>
        </div>
        <aside className="demo-context">
          <h3>Context ready</h3>
          <div><i aria-hidden="true">✓</i><span><strong>Useful next step</strong>{scenario.context}</span></div>
          <div><i aria-hidden="true">✓</i><span><strong>Business-aware answer</strong>Grounded in the information the business has approved.</span></div>
          <div><i aria-hidden="true">✓</i><span><strong>Team stays in control</strong>Take over with the conversation attached.</span></div>
        </aside>
      </div>
    </div>
  );
}
